// GpxFix Chart Module
// Provides chart initialization and vertical line plugin functionality
(function () {
    'use strict';

    // Export chart utilities globally
    window.GpxFixChart = window.GpxFixChart || {};

    // Store vertical line plugins by id
    const verticalLinePlugin = new Array(3);

    // Local storage key for legend visibility state
    const LEGEND_STATE_KEY = 'gpxfix_chart_legend_state';

    /**
     * Save legend visibility state to local storage
     * @param {Object} chart - Chart.js instance
     */
    function saveLegendState(chart) {
        if (!chart || !chart.data || !chart.data.datasets) return;

        const state = {};
        chart.data.datasets.forEach(function(dataset, index) {
            if (dataset.label) {
                // Use isDatasetVisible to get the actual visibility state (returns true if visible)
                // Store the inverse (hidden state) for each dataset by label
                const isVisible = chart.isDatasetVisible(index);
                state[dataset.label] = !isVisible; // true = hidden, false = visible
            }
        });

        try {
            localStorage.setItem(LEGEND_STATE_KEY, JSON.stringify(state));
        } catch (e) {
            console.warn('Failed to save legend state to localStorage:', e);
        }
    }

    /**
     * Restore legend visibility state from local storage
     * @returns {Object} Saved state object or empty object if not found
     */
    function restoreLegendState() {
        try {
            const saved = localStorage.getItem(LEGEND_STATE_KEY);
            const parsedState = saved ? JSON.parse(saved) : {};
            return parsedState;
        } catch (e) {
            console.warn('Failed to restore legend state from localStorage:', e);
            return {};
        }
    }

    /**
     * Apply saved legend state to chart datasets
     * @param {Array} datasets - Chart datasets array
     * @returns {boolean} True if default state was applied (needs to be saved after chart creation)
     */
    function applyLegendState(datasets) {
        const savedState = restoreLegendState();

        if (!datasets) return false;

        // If no saved state exists, apply default visibility (hide cadence and power)
        if (Object.keys(savedState).length === 0) {
            datasets.forEach(function(dataset) {
                if (dataset.label) {
                    // Default: hide cadence and power, show all others
                    const shouldHide = dataset.label === 'Cadence' || dataset.label === 'Power';
                    dataset.hidden = shouldHide;
                }
            });

            return true; // Indicates default was applied, needs to be saved
        }

        // Apply saved state from localStorage
        datasets.forEach(function(dataset) {
            if (dataset.label && savedState.hasOwnProperty(dataset.label)) {
                dataset.hidden = savedState[dataset.label];
            }
        });

        return false; // Saved state was applied, no need to save again
    }
        
    function setPlugin(pluginId) {
        verticalLinePlugin[pluginId] = {
            id: 'verticalLine' + pluginId,
            afterDraw(chart) {
                const lineX = chart.config.options.plugins.verticalLine?.xValue;
                if (lineX === undefined) return;
                const xScale = chart.scales.x;
                const xPos = xScale.getPixelForValue(lineX);
                const topY = chart.chartArea.top;
                const bottomY = chart.chartArea.bottom;

                const ctx = chart.ctx;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(xPos, topY);
                ctx.lineTo(xPos, bottomY);
                ctx.strokeStyle = '#333333';
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.restore();
            }
        };
    }

    /**
     * Get the vertical line plugin instance
     * @param {number} pluginId - Plugin identifier
     * @returns {Object} Plugin instance
     */
    function getPlugin(pluginId) {
        if (!verticalLinePlugin[pluginId]) {
            setPlugin(pluginId);
        }
        return verticalLinePlugin[pluginId];
    }

    /**
     * Build chart datasets from coordinate data
     * @param {Array} coordinates - Array of coordinate data [[{lat,lon}, hr, time, ele, speed, cad], ...]
     * @param {boolean} hasHeartRate - Whether activity has heart rate data
     * @returns {Object} Object with labels, datasets, and data availability flags
     */
    function buildChartDatasets(coordinates, hasHeartRate) {
        const len = coordinates.length;
        // Pre-size arrays to avoid repeated growth
        const labels = new Array(len);
        const elevationData = new Array(len);
        const heartRateData = new Array(len);
        const speedData = new Array(len);
        const cadenceData = new Array(len);
        const powerData = new Array(len);

        // Track availability flags in a single pass to avoid 5 extra .some/.every scans
        let hasElevationNonZero = false;
        let hasElevationAnyValue = false;
        let hasHrAny = false;
        let hasSpeedAny = false;
        let hasSpeedNonZero = false;
        let hasCadenceAny = false;
        let hasPowerAny = false;
        let maxSpeed = 0;
        let totalSpeed = 0;
        let speedCount = 0;

        for (let idx = 0; idx < len; idx++) {
            const coord = coordinates[idx];
            labels[idx] = idx + 1;

            const ele = coord[3];
            const hr = coord[1];
            const spd = coord[4];
            const cad = coord[5];
            const pwr = coord[6];

            const eleVal = (ele !== null && ele !== undefined) ? ele : null;
            const hrVal = (hr !== null && hr !== undefined) ? hr : null;
            const spdVal = (spd !== null && spd !== undefined) ? spd : null;
            const cadVal = (cad !== null && cad !== undefined) ? cad : null;
            const pwrVal = (pwr !== null && pwr !== undefined) ? pwr : null;

            elevationData[idx] = eleVal;
            heartRateData[idx] = hrVal;
            speedData[idx] = spdVal;
            cadenceData[idx] = cadVal;
            powerData[idx] = pwrVal;

            if (eleVal !== null) {
                hasElevationAnyValue = true;
                if (eleVal !== 0) hasElevationNonZero = true;
            }
            if (hrVal !== null) hasHrAny = true;
            if (spdVal !== null) {
                hasSpeedAny = true;
                if (spdVal > maxSpeed) maxSpeed = spdVal;
                if (spdVal !== 0) hasSpeedNonZero = true;
                totalSpeed += spdVal;
                speedCount++;
            }
            if (cadVal !== null) hasCadenceAny = true;
            if (pwrVal !== null && pwrVal > 0) hasPowerAny = true;
        }

        // Calculate average speed
        const avgSpeed = speedCount > 0 ? totalSpeed / speedCount : 0;

        // Match prior semantics: elevation requires at least one non-zero value
        const hasElevation = hasElevationNonZero && hasElevationAnyValue;
        const hasHr = !!hasHeartRate && hasHrAny;
        const hasSpeed = hasSpeedAny && hasSpeedNonZero;
        const hasCadence = hasCadenceAny;
        const hasPower = hasPowerAny;

        const datasets = [];

        if (hasHr) {
            datasets.push({
                label: 'Heart Rate',
                data: heartRateData,
                borderColor: 'rgba(219, 59, 0, 1)',
                backgroundColor: 'transparent',
                fill: false,
                tension: 0.6,
                pointRadius: 0,
                borderWidth: 1.5,
                yAxisID: 'y2'
            });
        }

        if (hasSpeed) {
            datasets.push({
                label: 'Speed',
                data: speedData,
                yAxisID: 'y3',
                borderColor: 'rgba(13, 85, 120, 1)',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                fill: false,
                tension: 0.6,
                cubicInterpolationMode: 'monotone',
                borderWidth: 0.5,
                pointRadius: 0
            });
        }

        if (hasCadence) {
            datasets.push({
                label: 'Cadence',
                data: cadenceData,
                yAxisID: 'y4',
                borderColor: '#FF00FF',
                backgroundColor: 'rgba(255, 255, 255, 0)',
                fill: false,
                tension: 0.6,
                borderWidth: 1,
                pointRadius: 0
            });
        }

        if (hasPower) {
            datasets.push({
                label: 'Power',
                data: powerData,
                yAxisID: 'y5',
                borderColor: 'rgba(255, 165, 0, 1)',
                backgroundColor: 'rgba(255, 165, 0, 0.1)',
                fill: false,
                tension: 0.6,
                borderWidth: 1.5,
                pointRadius: 0
            });
        }

        if (hasElevation) {
            datasets.push({
                label: 'Elevation',
                data: elevationData,
                yAxisID: 'y1',
                borderColor: 'rgba(155, 155, 155, 1)',
                backgroundColor: 'rgba(200, 200, 200, 0.6)',
                fill: true,
                tension: 0.6,
                borderWidth: 0.2,
                pointRadius: 0
            });
        }

        return {
            labels: labels,
            datasets: datasets,
            hasElevation: hasElevation,
            hasHeartRate: hasHr,
            hasSpeed: hasSpeed,
            hasCadence: hasCadence,
            hasPower: hasPower,
            heartRateData: heartRateData,
            maxSpeed: maxSpeed,
            avgSpeed: avgSpeed
        };
    }

    /**
     * Get standard chart options
     * @param {boolean} showLegend - Whether to show the legend
     * @param {number} maxSpeed - Maximum speed to set y-axis for speed
     * @param {number} avgSpeed - Average speed of the activity
     * @returns {Object} Chart.js options object
     */
    function getChartOptions(showLegend, maxSpeed, avgSpeed) {
        
        showLegend = showLegend !== false; // default to true
        const speedAxisOptions = {
            type: 'linear',
            min: 0,
            ticks: { display: false },
            border: { display: true },
            grid: { display: false, drawOnChartArea: false },
            title: { display: false }
        };

        if (maxSpeed > 0 && maxSpeed < 20) {
            speedAxisOptions.max = 20;
        } else if (avgSpeed !== undefined && avgSpeed < 50 && maxSpeed > 100) {
            // If average speed was less than 50 but max speed was above 100, cap the y-axis at 100
            speedAxisOptions.max = 100;        
        } else if (avgSpeed !== undefined && avgSpeed < 10 && maxSpeed > 50) {
            // If average speed was less than 10 but max speed was above 50, cap the y-axis at 50
            speedAxisOptions.max = 50;
        } 

        /* y1=elevation, y2=HR, y3=speed, y4=cadence, y5=power */
        return {
            layout: {
                padding: { left: -15 }
            },
            responsive: true,
            maintainAspectRatio: false,
            // Performance: disable animations to speed up redraws (e.g. moveVerticalLineTo)
            animation: false,
            animations: { colors: false, x: false, y: false },
            spanGaps: true,
            plugins: {
                legend: {
                    display: showLegend,
                    position: 'top',
                    align: 'center',
                    labels: {
                        lineWidth: 5,
                        color: 'black',
                        font: { size: 12 }
                    },
                    onClick: function(e, legendItem, legend) {
                        // Default toggle behavior
                        const index = legendItem.datasetIndex;
                        const chart = legend.chart;

                        if (chart.isDatasetVisible(index)) {
                            chart.hide(index);
                            legendItem.hidden = true;
                        } else {
                            chart.show(index);
                            legendItem.hidden = false;
                        }

                        // Save the new state to local storage
                        saveLegendState(chart);
                    }
                },
                tooltip: { enabled: true },
                title: {
                    display: false,
                    font: { size: 12 },
                    padding: { top: 10, bottom: 5 }
                },
                verticalLine: { xValue: 1 }
            },
            scales: {
                x: {
                    ticks: { display: false, color: 'white', autoSkip: true, maxTicksLimit: 30, font: { size: 1 } },
                    grid: { display: true },
                    title: { display: false },
                    border: { display: true }
                },
                y1: {                    
                    beginAtZero: true,
                    ticks: { display: false },
                    grid: { display: false, drawOnChartArea: false },
                    title: { display: false },
                    border: { display: false }
                },
                y2: {
                    type: 'linear',
                    min: 30,
                    ticks: { display: false },
                    border: { display: false },
                    grid: { display: false, drawOnChartArea: false },
                    title: { display: false }
                },
                y3: speedAxisOptions,
                y4: {
                    type: 'linear',
                    position: 'right',
                    min: 0,
                    max: 200,
                    ticks: { display: false },
                    border: { display: true },
                    grid: { display: false, drawOnChartArea: false },
                    title: { display: false }
                },
                y5: {
                    type: 'linear',
                    position: 'right',
                    min: 0,
                    ticks: { display: false },
                    border: { display: true },
                    grid: { display: false, drawOnChartArea: false },
                    title: { display: false }
                }
            }
        };
    }

    /**
     * Move vertical line to a specific x position on the chart
     * @param {Object} chart - Chart.js instance
     * @param {number} xValue - X position to move the line to
     */
    function moveVerticalLineTo(chart, xValue) {
        if (chart && chart.options && chart.options.plugins) {
            const vl = chart.options.plugins.verticalLine;
            if (vl.xValue === xValue) return; // no-op if unchanged
            vl.xValue = xValue;
            chart.draw();
        }
    }

    /**
     * Move vertical lines on multiple charts (for compare mode)
     * @param {Array} charts - Array of Chart.js instances
     * @param {number} xValue - X position to move the lines to
     */
    function moveVerticalLineToAll(charts, xValue) {
        if (!charts || !Array.isArray(charts)) return;
        charts.forEach(function(chart) {
            moveVerticalLineTo(chart, xValue);
        });
    }

    // Export functions
    window.GpxFixChart = {
        setPlugin: setPlugin,
        getPlugin: getPlugin,
        buildChartDatasets: buildChartDatasets,
        getChartOptions: getChartOptions,
        moveVerticalLineTo: moveVerticalLineTo,
        moveVerticalLineToAll: moveVerticalLineToAll,
        verticalLinePlugin: verticalLinePlugin,
        saveLegendState: saveLegendState,
        restoreLegendState: restoreLegendState,
        applyLegendState: applyLegendState
    };

    // Keep backward compatibility with global variables
    window.verticalLinePlugin = verticalLinePlugin;
    window.SetPlugin = setPlugin;
    window.moveVerticalLineTo = moveVerticalLineTo;
    window.moveVerticalLineToAll = moveVerticalLineToAll;

    /**
     * Initialize or update a chart on a canvas element
     * @param {Object} state - State object containing coordinates, hasHeartRate, and chart reference
     * @param {number} pluginId - Plugin identifier for vertical line
     * @param {string} canvasId - Canvas element ID (default: 'chart')
     * @returns {Object|null} Chart instance or null
     */
    function initializeChart(state, pluginId, canvasId) {
        canvasId = canvasId || 'chart';

        var ctx = document.getElementById(canvasId);
        if (!ctx) return null;

        // If there are no coordinates, destroy any existing chart and hide the canvas
        if (!state.coordinates || state.coordinates.length === 0) {
            if (state.chart) {
                try { state.chart.destroy(); } catch (e) { /* ignore */ }
                state.chart = null;
            }
            ctx.style.display = 'none';
            return null;
        }

        // Destroy existing chart instance before creating a new one
        if (state.chart) {
            try { state.chart.destroy(); } catch (e) { /* ignore */ }
            state.chart = null;
        }

        // Build datasets using shared function
        var chartData = buildChartDatasets(state.coordinates, state.hasHeartRate);

        // If no data at all, hide the chart and return
        if (chartData.datasets.length === 0) {
            ctx.style.display = 'none';
            return null;
        }

        // Apply saved legend state to datasets before creating chart
        var shouldSaveDefaults = applyLegendState(chartData.datasets);

        // Ensure canvas is visible when we do have data
        ctx.style.display = '';

        // Get plugin and options
        setPlugin(pluginId);
        var options = getChartOptions(true, chartData.maxSpeed, chartData.avgSpeed);

        state.chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: chartData.labels,
                datasets: chartData.datasets
            },
            plugins: [verticalLinePlugin[pluginId]],
            options: options
        });

        // If first heart rate datum is negative, remove HR dataset
        if (chartData.heartRateData && chartData.heartRateData.length > 0 && chartData.heartRateData[0] < 0 && state.chart) {
            var idx = state.chart.data.datasets.findIndex(function (d) { return /heart.*rate/i.test(d.label); });
            if (idx >= 0) {
                state.chart.data.datasets.splice(idx, 1);
                state.chart.update();
            }
        }

        // Save default state to localStorage if it was just applied
        if (shouldSaveDefaults && state.chart) {
            saveLegendState(state.chart);
        }

        $("#timeSlider").trigger('change');

        return state.chart;
    }

    /**
     * Create a chart on any canvas element (generic version)
     * @param {string} canvasId - Canvas element ID
     * @param {Array} coordinates - Coordinate data array
     * @param {boolean} hasHeartRate - Whether activity has heart rate data
     * @param {number} pluginId - Plugin identifier
     * @returns {Object|null} Chart instance or null
     */
    function createChart(canvasId, coordinates, hasHeartRate, pluginId) {
        var ctx = document.getElementById(canvasId);
        if (!ctx) return null;

        if (!coordinates || coordinates.length === 0) {
            ctx.style.display = 'none';
            return null;
        }

        var chartData = buildChartDatasets(coordinates, hasHeartRate);

        if (chartData.datasets.length === 0) {
            ctx.style.display = 'none';
            return null;
        }

        // Apply saved legend state to datasets before creating chart
        var shouldSaveDefaults = applyLegendState(chartData.datasets);

        ctx.style.display = '';
        setPlugin(pluginId);
        var options = getChartOptions(true, chartData.maxSpeed, chartData.avgSpeed);

        var chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: chartData.labels,
                datasets: chartData.datasets
            },
            plugins: [verticalLinePlugin[pluginId]],
            options: options
        });

        // Save default state to localStorage if it was just applied
        if (shouldSaveDefaults && chart) {
            saveLegendState(chart);
        }

        return chart;
    }

    // Export initializeChart and createChart
    window.GpxFixChart.initializeChart = initializeChart;
    window.GpxFixChart.createChart = createChart;

    // Global function for backward compatibility
    window.initializeChart = initializeChart;

    // ---- Bad HR Segment Selection ----

    // Stores user-selected bad HR segments as [{startIndex, endIndex}, ...]
    var badHrSegments = [];
    var badSegmentDragState = { active: false, enabled: false, startIndex: null };

    /**
     * Chart.js plugin that draws shaded red overlays for selected bad HR segments
     * and a semi-transparent overlay while the user is dragging.
     */
    var badSegmentHighlightPlugin = {
        id: 'badSegmentHighlight',
        afterDraw: function (chart) {
            if (!badSegmentDragState.enabled) return;
            var ctx = chart.ctx;
            var xScale = chart.scales.x;
            var topY = chart.chartArea.top;
            var bottomY = chart.chartArea.bottom;

            // Draw confirmed bad segments
            ctx.save();
            for (var i = 0; i < badHrSegments.length; i++) {
                var seg = badHrSegments[i];
                var x1 = xScale.getPixelForValue(seg.startIndex);
                var x2 = xScale.getPixelForValue(seg.endIndex);
                ctx.fillStyle = 'rgba(220, 50, 50, 0.2)';
                ctx.fillRect(x1, topY, x2 - x1, bottomY - topY);
                // Border lines
                ctx.strokeStyle = 'rgba(220, 50, 50, 0.6)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x1, topY); ctx.lineTo(x1, bottomY);
                ctx.moveTo(x2, topY); ctx.lineTo(x2, bottomY);
                ctx.stroke();
            }

            // Draw active drag region
            if (badSegmentDragState.active && badSegmentDragState.startIndex !== null && badSegmentDragState.currentIndex !== undefined) {
                var ds = Math.min(badSegmentDragState.startIndex, badSegmentDragState.currentIndex);
                var de = Math.max(badSegmentDragState.startIndex, badSegmentDragState.currentIndex);
                var dx1 = xScale.getPixelForValue(ds);
                var dx2 = xScale.getPixelForValue(de);
                ctx.fillStyle = 'rgba(220, 50, 50, 0.12)';
                ctx.fillRect(dx1, topY, dx2 - dx1, bottomY - topY);
                ctx.strokeStyle = 'rgba(220, 50, 50, 0.4)';
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(dx1, topY); ctx.lineTo(dx1, bottomY);
                ctx.moveTo(dx2, topY); ctx.lineTo(dx2, bottomY);
                ctx.stroke();
                ctx.setLineDash([]);
            }
            ctx.restore();
        }
    };

    /**
     * Get the data index from a mouse event on the chart canvas.
     */
    function getIndexFromEvent(chart, evt) {        
        if (!chart || !chart.canvas) return 0;
        var rect = chart.canvas.getBoundingClientRect();
        var x = evt.clientX - rect.left;
        var xScale = chart.scales.x;
        var val = xScale.getValueForPixel(x);
        // Clamp to valid label range
        var maxIdx = chart.data.labels.length - 1;
        return Math.max(0, Math.min(maxIdx, Math.round(val)));
    }

    /**
     * Enable bad segment selection mode on a chart.
     * Attaches mousedown/mousemove/mouseup listeners to the canvas.
     * @param {Object} chart - Chart.js instance
     * @param {Function} onSegmentAdded - Callback when a segment is added
     */
    function enableBadSegmentSelection(chart, onSegmentAdded) {
        if (!chart || !chart.canvas) return;
        badSegmentDragState.enabled = true;

        // Register plugin if not already
        if (!chart.config.plugins.some(function (p) { return p.id === 'badSegmentHighlight'; })) {
            chart.config.plugins.push(badSegmentHighlightPlugin);
            chart.update();
        }

        var canvas = chart.canvas;
        canvas.style.cursor = 'crosshair';

        // Remove old listeners if any
        if (canvas._badSegMouseDown) {
            canvas.removeEventListener('mousedown', canvas._badSegMouseDown);
            canvas.removeEventListener('mousemove', canvas._badSegMouseMove);
            canvas.removeEventListener('mouseup', canvas._badSegMouseUp);
            canvas.removeEventListener('mouseleave', canvas._badSegMouseLeave);
        }

        canvas._badSegMouseDown = function (e) {
            if (!badSegmentDragState.enabled) return;
            badSegmentDragState.active = true;
            badSegmentDragState.startIndex = getIndexFromEvent(chart, e);
            badSegmentDragState.currentIndex = badSegmentDragState.startIndex;
        };

        canvas._badSegMouseMove = function (e) {
            if (!badSegmentDragState.active) return;
            badSegmentDragState.currentIndex = getIndexFromEvent(chart, e);
            chart.draw();
        };

        canvas._badSegMouseUp = function (e) {
            if (!badSegmentDragState.active) return;
            badSegmentDragState.active = false;
            var endIndex = getIndexFromEvent(chart, e);
            var s = Math.min(badSegmentDragState.startIndex, endIndex);
            var en = Math.max(badSegmentDragState.startIndex, endIndex);

            // Only add if the selection covers at least 3 points
            if (en - s >= 3) {
                badHrSegments.push({ startIndex: s, endIndex: en });
                chart.draw();
                if (typeof onSegmentAdded === 'function') onSegmentAdded(badHrSegments);
            }
            badSegmentDragState.startIndex = null;
            badSegmentDragState.currentIndex = undefined;
        };

        canvas._badSegMouseLeave = function () {
            if (badSegmentDragState.active) {
                badSegmentDragState.active = false;
                badSegmentDragState.startIndex = null;
                badSegmentDragState.currentIndex = undefined;
                chart.draw();
            }
        };

        canvas.addEventListener('mousedown', canvas._badSegMouseDown);
        canvas.addEventListener('mousemove', canvas._badSegMouseMove);
        canvas.addEventListener('mouseup', canvas._badSegMouseUp);
        canvas.addEventListener('mouseleave', canvas._badSegMouseLeave);
    }

    /**
     * Disable bad segment selection mode.
     */
    function disableBadSegmentSelection(chart) {
        badSegmentDragState.enabled = false;
        badSegmentDragState.active = false;
        if (chart && chart.canvas) {
            chart.canvas.style.cursor = '';
            if (chart.canvas._badSegMouseDown) {
                chart.canvas.removeEventListener('mousedown', chart.canvas._badSegMouseDown);
                chart.canvas.removeEventListener('mousemove', chart.canvas._badSegMouseMove);
                chart.canvas.removeEventListener('mouseup', chart.canvas._badSegMouseUp);
                chart.canvas.removeEventListener('mouseleave', chart.canvas._badSegMouseLeave);
            }
        }
        // Remove plugin by splicing in-place (config.plugins is read-only)
        if (chart && chart.config && chart.config.plugins) {
            var plugins = chart.config.plugins;
            for (var pi = plugins.length - 1; pi >= 0; pi--) {
                if (plugins[pi] && plugins[pi].id === 'badSegmentHighlight') {
                    plugins.splice(pi, 1);
                }
            }
            chart.update();
        }
    }

    /**
     * Remove a specific bad segment by index.
     */
    function removeBadSegment(chart, segmentIdx) {
        if (segmentIdx >= 0 && segmentIdx < badHrSegments.length) {
            badHrSegments.splice(segmentIdx, 1);
            if (chart) chart.draw();
        }
    }

    /**
     * Clear all selected bad segments.
     */
    function clearBadSegments(chart) {
        badHrSegments = [];
        if (chart) chart.draw();
    }

    /**
     * Get a copy of the current bad segments.
     */
    function getBadSegments() {
        return badHrSegments.slice();
    }

    // Export bad segment selection functions
    window.GpxFixChart.enableBadSegmentSelection = enableBadSegmentSelection;
    window.GpxFixChart.disableBadSegmentSelection = disableBadSegmentSelection;
    window.GpxFixChart.removeBadSegment = removeBadSegment;
    window.GpxFixChart.clearBadSegments = clearBadSegments;
    window.GpxFixChart.getBadSegments = getBadSegments;

    // ---- Crop Range Selection ----

    var cropRange = { startIndex: null, endIndex: null };
    var cropRangeDragState = { active: false, enabled: false, startIndex: null, currentIndex: undefined };

    /**
     * Chart.js plugin that draws a shaded region and boundary lines for the selected crop range.
     */
    var cropRangePlugin = {
        id: 'cropRangeHighlight',
        afterDraw: function (chart) {
            if (!cropRangeDragState.enabled) return;
            var ctx = chart.ctx;
            var xScale = chart.scales.x;
            var topY = chart.chartArea.top;
            var bottomY = chart.chartArea.bottom;

            ctx.save();

            // Draw confirmed crop range
            if (cropRange.startIndex !== null && cropRange.endIndex !== null) {
                var x1 = xScale.getPixelForValue(cropRange.startIndex);
                var x2 = xScale.getPixelForValue(cropRange.endIndex);
                ctx.fillStyle = 'rgba(220, 100, 0, 0.18)';
                ctx.fillRect(x1, topY, x2 - x1, bottomY - topY);
                ctx.strokeStyle = 'rgba(220, 100, 0, 0.7)';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.moveTo(x1, topY); ctx.lineTo(x1, bottomY);
                ctx.moveTo(x2, topY); ctx.lineTo(x2, bottomY);
                ctx.stroke();
            }

            // Draw active drag region
            if (cropRangeDragState.active && cropRangeDragState.startIndex !== null && cropRangeDragState.currentIndex !== undefined) {
                var ds = Math.min(cropRangeDragState.startIndex, cropRangeDragState.currentIndex);
                var de = Math.max(cropRangeDragState.startIndex, cropRangeDragState.currentIndex);
                var dx1 = xScale.getPixelForValue(ds);
                var dx2 = xScale.getPixelForValue(de);
                ctx.fillStyle = 'rgba(220, 100, 0, 0.10)';
                ctx.fillRect(dx1, topY, dx2 - dx1, bottomY - topY);
                ctx.strokeStyle = 'rgba(220, 100, 0, 0.5)';
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 4]);
                ctx.beginPath();
                ctx.moveTo(dx1, topY); ctx.lineTo(dx1, bottomY);
                ctx.moveTo(dx2, topY); ctx.lineTo(dx2, bottomY);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            ctx.restore();
        }
    };

    /**
     * Enable crop range selection mode on a chart.
     * @param {Object} chart - Chart.js instance
     * @param {Function} onRangeSelected - Callback({startIndex, endIndex}) when range is selected
     */
    function enableCropRangeSelection(chart, onRangeSelected) {
        if (!chart || !chart.canvas) return;
        cropRangeDragState.enabled = true;
        cropRange.startIndex = null;
        cropRange.endIndex = null;

        if (!chart.config.plugins.some(function (p) { return p.id === 'cropRangeHighlight'; })) {
            chart.config.plugins.push(cropRangePlugin);
            chart.update();
        }

        var canvas = chart.canvas;
        canvas.style.cursor = 'col-resize';

        if (canvas._cropMouseDown) {
            canvas.removeEventListener('mousedown', canvas._cropMouseDown);
            canvas.removeEventListener('mousemove', canvas._cropMouseMove);
            canvas.removeEventListener('mouseup', canvas._cropMouseUp);
            canvas.removeEventListener('mouseleave', canvas._cropMouseLeave);
        }

        canvas._cropMouseDown = function (e) {
            if (!cropRangeDragState.enabled) return;
            cropRangeDragState.active = true;
            cropRangeDragState.startIndex = getIndexFromEvent(chart, e);
            cropRangeDragState.currentIndex = cropRangeDragState.startIndex;
        };

        canvas._cropMouseMove = function (e) {
            if (!cropRangeDragState.active) return;
            cropRangeDragState.currentIndex = getIndexFromEvent(chart, e);
            chart.draw();
        };

        canvas._cropMouseUp = function (e) {
            if (!cropRangeDragState.active) return;
            cropRangeDragState.active = false;
            var endIndex = getIndexFromEvent(chart, e);
            var s = Math.min(cropRangeDragState.startIndex, endIndex);
            var en = Math.max(cropRangeDragState.startIndex, endIndex);

            if (en > s) {
                cropRange.startIndex = s;
                cropRange.endIndex = en;
                chart.draw();
                if (typeof onRangeSelected === 'function') onRangeSelected({ startIndex: s, endIndex: en });
            }
            cropRangeDragState.startIndex = null;
            cropRangeDragState.currentIndex = undefined;
        };

        canvas._cropMouseLeave = function () {
            if (cropRangeDragState.active) {
                cropRangeDragState.active = false;
                cropRangeDragState.startIndex = null;
                cropRangeDragState.currentIndex = undefined;
                chart.draw();
            }
        };

        canvas.addEventListener('mousedown', canvas._cropMouseDown);
        canvas.addEventListener('mousemove', canvas._cropMouseMove);
        canvas.addEventListener('mouseup', canvas._cropMouseUp);
        canvas.addEventListener('mouseleave', canvas._cropMouseLeave);
    }

    /**
     * Disable crop range selection mode.
     */
    function disableCropRangeSelection(chart) {
        cropRangeDragState.enabled = false;
        cropRangeDragState.active = false;
        cropRange.startIndex = null;
        cropRange.endIndex = null;
        if (chart && chart.canvas) {
            chart.canvas.style.cursor = '';
            if (chart.canvas._cropMouseDown) {
                chart.canvas.removeEventListener('mousedown', chart.canvas._cropMouseDown);
                chart.canvas.removeEventListener('mousemove', chart.canvas._cropMouseMove);
                chart.canvas.removeEventListener('mouseup', chart.canvas._cropMouseUp);
                chart.canvas.removeEventListener('mouseleave', chart.canvas._cropMouseLeave);
            }
        }
        if (chart && chart.config && chart.config.plugins) {
            var plugins = chart.config.plugins;
            for (var pi = plugins.length - 1; pi >= 0; pi--) {
                if (plugins[pi] && plugins[pi].id === 'cropRangeHighlight') {
                    plugins.splice(pi, 1);
                }
            }
            if (chart.canvas) chart.update();
        }
    }

    /**
     * Get the currently selected crop range.
     * @returns {{startIndex: number|null, endIndex: number|null}}
     */
    function getCropRange() {
        return { startIndex: cropRange.startIndex, endIndex: cropRange.endIndex };
    }

    /**
     * Clear the current crop range selection.
     */
    function clearCropRange(chart) {
        cropRange.startIndex = null;
        cropRange.endIndex = null;
        if (chart) chart.draw();
    }

    // Export crop range selection functions
    window.GpxFixChart.enableCropRangeSelection = enableCropRangeSelection;
    window.GpxFixChart.disableCropRangeSelection = disableCropRangeSelection;
    window.GpxFixChart.getCropRange = getCropRange;
    window.GpxFixChart.clearCropRange = clearCropRange;

})();
